<template lang="pug">
div
    //- every action of the row in one "…" menu (merchant portal Orders), tasks#211
    v-menu(v-if="actions.length" offset-y left)
        template(#activator="{ on, attrs }")
            v-btn(
                icon
                small
                v-bind="attrs"
                :aria-label="$te('labels.actions') ? $t('labels.actions') : 'Actions'"
                v-on="on")
                v-icon(small) more_horiz
        v-list
            template(v-for="action in actions")
                v-list-item(
                    v-if="action.href"
                    :key="action.key"
                    :href="action.href"
                    target="_blank")
                    v-list-item-icon
                        v-icon {{ action.icon }}
                    v-list-item-title {{ action.label }}
                v-list-item(
                    v-else-if="action.to"
                    :key="action.key"
                    :to="action.to")
                    v-list-item-icon
                        v-icon {{ action.icon }}
                    v-list-item-title {{ action.label }}
                v-list-item(
                    v-else
                    :key="action.key"
                    @click="action.run")
                    v-list-item-icon
                        v-icon {{ action.icon }}
                    v-list-item-title {{ action.label }}
    //- the dialogs exist only while open: a list of 50 rows no longer builds 250 hidden dialogs
    //- (and their option requests) on load, and the heavy ones load on demand (tasks#211)
    strong-confirm-modal(
        v-if="strongConfirm"
        v-model="strongConfirm"
        :title="strong.title"
        :text="strong.text"
        :processing="processing"
        :approve-color="strong.approveColor"
        @onApprove="strongAction"
        @onCancel="strongConfirm = false")
    confirm-modal(
        v-if="confirmDeliverSendOutModal"
        :value="confirmDeliverSendOutModal"
        :text="$t('orders.do_you_want_send_out_this_order')"
        :processing="processing"
        @onApprove="setDeliverySendOut()"
        @onCancel="cancelDeliverySendOut")
    //- these two stay mounted once opened: they finish their requests after closing
    pedalme-delivery-modal(
        v-if="item.deliveryJob && pedalmeMounted"
        v-model="pedalmeDialog"
        :delivery-job="item.deliveryJob"
        @onApprove="sendPedalme"
    )
    order-cancel-modal(
        v-if="orderCancelMounted"
        v-model="orderCancelConfirm"
        :order-id="item.id"
        :has-firm-payment="item.priceSumFirm !== '0.00'"
        @order-cancelled="onCancelOrder"
    )
</template>

<script>
import routeType from '@/router/routeType'
import ConfirmModal from '@/components/confirm/ConfirmModal'
import StrongConfirmModal from '@/components/confirm/StrongConfirmModal'
import OrdersApi from '@/api/RestApi/OrdersApiClient'
import DeliveriesApi from '@/api/RestApi/DeliveriesApiClient'
import Notification from '@/services/Notification/Notification'

const modalBase = {
    title: '',
    text: '',
    action: '',
    approveColor: null,
}

export default {
    components: {
        ConfirmModal,
        StrongConfirmModal,
        PedalmeDeliveryModal: () =>
            import('@/components/pages/canteen/PedalmeDeliveryModal'),
        OrderCancelModal: () =>
            import('@/components/pages/orders/OrderCancelModal'),
    },
    props: {
        item: Object,
        updateTableItems: Function,
    },
    data() {
        return {
            confirmDeliverSendOutModal: false,
            confirmCancelOrderModal: false,
            pedalmeDialog: false,
            deliverySendOutItem: null,
            cancelOrderItem: null,
            processing: false,
            routeType: routeType,
            strongConfirm: false,
            strong: { ...modalBase },
            orderCancelConfirm: false,
            pedalmeMounted: false,
            orderCancelMounted: false,
        }
    },
    computed: {
        isCanteen() {
            return (
                Boolean(this.item.cloudcanteen) ||
                Boolean(this.item.residentialcanteen)
            )
        },
        canConfirm() {
            return (
                this.item.status !== 'approved' &&
                this.item.status !== 'init-payment' &&
                this.item.isDeliveryProcessed &&
                !this.isCanteen
            )
        },
        canCancel() {
            return (
                this.item.status !== 'cancelled' &&
                this.item.status !== 'cancelled-after-approval' &&
                this.item.status !== 'init-payment' &&
                this.item.isDeliveryProcessed &&
                !this.isCanteen
            )
        },
        canSend() {
            return (
                this.item.deliveryJob &&
                this.item.deliveryJob.id &&
                this.item.deliveryJob.status === 'initialized' &&
                this.item.isDeliveryProcessed &&
                !this.isCanteen
            )
        },
        isCanteenSuborder() {
            return this.isCanteen && Boolean(this.item.userProfile)
        },
        // the same actions, conditions and order as the former icon row
        actions() {
            const preview = this.$router.resolve({
                name: routeType.ORDER_PREVIEW_EMAIL,
                params: { id: this.item.id },
            }).href
            return [
                this.canConfirm && {
                    key: 'approve',
                    icon: 'go-approve',
                    label: this.$t('labels.approve_order'),
                    run: this.confirmOrderModal,
                },
                this.canCancel && {
                    key: 'cancel',
                    icon: 'go-cancel',
                    label: this.$t('labels.cancel_order'),
                    run: () => (this.orderCancelConfirm = true),
                },
                this.item.deliveryJob && {
                    key: 'pedalme',
                    icon: 'pedal_bike',
                    label: this.$t('labels.pedalme_tooltip'),
                    run: () => (this.pedalmeDialog = true),
                },
                this.canSend && {
                    key: 'send',
                    icon: 'send',
                    label: this.$t('labels.delivery_send_out'),
                    run: () => this.deliverySendOutModal(this.item),
                },
                this.item.canYouCreateDeliveryJob &&
                    !this.isCanteen && {
                        key: 'delivery',
                        icon: 'swap_horiz',
                        label: this.$t('labels.create_delivery_job'),
                        to: {
                            name: routeType.DELIVERY_CREATE,
                            params: { id: this.item.id },
                        },
                    },
                !this.isCanteenSuborder && {
                    key: 'resend',
                    icon: 'outgoing_mail',
                    label: this.$t('labels.order_mail_resend'),
                    run: this.resendMailModal,
                },
                {
                    key: 'preview',
                    icon: 'email',
                    label: this.$t('labels.order_mail_preview'),
                    href: preview,
                },
            ].filter(Boolean)
        },
    },
    watch: {
        pedalmeDialog(open) {
            if (open) this.pedalmeMounted = true
        },
        orderCancelConfirm(open) {
            if (open) this.orderCancelMounted = true
        },
    },
    methods: {
        strongAction() {
            this[this.strong.action]().then(() => {
                this.strongConfirm = false
            })
        },

        confirmOrderModal() {
            this.strong = {
                ...modalBase,
                title: this.$t('labels.approve_order'),
                text: this.$t(
                    'orders.do_you_really_want_to_approve_this_order',
                    { orderIdentifier: this.item.identifier }
                ),
                action: 'confirmOrder',
            }
            this.strongConfirm = true
        },

        confirmOrder() {
            return OrdersApi.approve(this.item.id)
        },

        onCancelOrder() {
            Notification.success(
                this.item.identifier,
                this.$t('notifications.entity_deleted.message', {
                    name: this.item.identifier,
                })
            )
        },

        resendMailModal() {
            this.strong = {
                ...modalBase,
                title: this.$t('labels.order_mail_resend'),
                text: this.$t(
                    'orders.do_you_really_want_to_resend_this_order_mail',
                    { orderIdentifier: this.item.identifier }
                ),
                action: 'resendMail',
            }
            this.strongConfirm = true
        },

        resendMail() {
            return OrdersApi.mailSend(this.item.id).then(() => {
                Notification.success(
                    this.item.identifier,
                    this.$t('notifications.order_mail_resent.message')
                )
            })
        },

        deliverySendOutModal(item) {
            this.confirmDeliverSendOutModal = true
            this.deliverySendOutItem = item.deliveryJob.id
        },

        setDeliverySendOut() {
            this.processing = true
            DeliveriesApi.sendOutDeliveryJob(this.deliverySendOutItem)
                .then(() => {
                    Notification.success(
                        this.deliverySendOutItem,
                        this.$t('notifications.order_delivery_sent_out.message')
                    ),
                        (this.confirmDeliverSendOutModal = false)
                    this.updateTableItems()
                })
                .catch((reason) => {
                    Notification.error('order send out error', reason)
                })
                .finally(() => {
                    this.processing = false
                })
        },

        cancelDeliverySendOut() {
            this.confirmDeliverSendOutModal = false
            this.deliverySendOutItem = null
        },

        sendPedalme(pickupTime, selection, size) {
            DeliveriesApi.sendToPedalme(pickupTime, selection, size)
                .then(() => {
                    Notification.success(
                        this.$t('notifications.pedalme.success')
                    )
                })
                .catch((err) => {
                    let message = err.response?.data?.errors?.join(';')
                    Notification.error(
                        this.$t('notifications.pedalme.fail', {
                            message,
                        })
                    )
                })
                .finally(() => {
                    this.pedalmeDialog = false
                })
        },
    },
}
</script>

<style scoped lang="scss"></style>
